import { addDays } from "date-fns";

import {
  UISubscriptionStatus,
  UserSubscriptionAction,
  SubscriptionCancelReason,
  SubscriptionCancelReasonOption,
  PaymentMethodChargeabilityStatus,
} from "common/types";
import { parseNumber } from "common/utils/numbers";
import { toDollars, toPercent } from "common/utils/formatting";
import { atMidnight, normalizeToDate, toDate, today } from "common/utils/dates";
import { Optional, TupleOfEntireUnion } from "common/utils/types";
import { Destination } from "common/router/types";

import type { StatusColor } from "client/common/components/content/Status";
import { ModalStatus } from "client/common/types";
import { urls } from "client/routes";

import {
  AcceptedRetentionOffer,
  DetailSubscription,
  ListSubscription,
  RetentionOfferCancelReason,
  RetentionOfferPreview,
  SubscriptionForm,
  SubscriptionPaymentMethod,
} from "../types";

// Mirror of subscriptions.constants.EDB_MIN_LEAD_DAYS (BE): the minimum lead time in days
// before an expected delivery date is treated as too soon to fulfill.
export const edbMinLeadDays = 7;

// Earliest expected delivery date a client may select on a v2 (EDB) subscription.
export const minSelectableDeliveryDate = () => addDays(today(), edbMinLeadDays);

export const genSubscriptionDiscountContent = <T extends any>(
  firstDiscount: string,
  recurringDiscount: string,
  {
    none,
    onlyFirst,
    onlyRecurring,
    both,
  }: {
    none: () => T;
    onlyFirst: (first: string) => T;
    onlyRecurring: (recurring: string) => T;
    both: (first: string, recurring: string) => T;
  },
) => {
  const hasFirst = !!parseNumber(firstDiscount, 0);
  const hasRecurring = !!parseNumber(recurringDiscount, 0);

  if (!hasFirst && !hasRecurring) {
    return none();
  }
  if (!hasRecurring && hasFirst) {
    return onlyFirst(toPercent(firstDiscount));
  }
  if ((hasRecurring && !hasFirst) || recurringDiscount === firstDiscount) {
    return onlyRecurring(toPercent(recurringDiscount));
  }
  return both(toPercent(firstDiscount), toPercent(recurringDiscount));
};

export const genSubscriptionMaxDiscountContent = <T extends any>(
  firstMax: Optional<number>,
  recurringMax: Optional<number>,
  {
    none,
    onlyFirst,
    onlyRecurring,
    both,
  }: {
    none: () => T;
    onlyFirst: (first: string) => T;
    onlyRecurring: (recurring: string) => T;
    both: (first: string, recurring: string) => T;
  },
) => {
  const hasFirst = !!firstMax;
  const hasRecurring = !!recurringMax;

  if (!hasFirst && !hasRecurring) {
    return none();
  }
  if (!hasRecurring && hasFirst) {
    return onlyFirst(toDollars(firstMax, { roundCents: true }));
  }
  if ((hasRecurring && !hasFirst) || firstMax === recurringMax) {
    return onlyRecurring(toDollars(recurringMax, { roundCents: true }));
  }
  return both(
    toDollars(firstMax, { roundCents: true }),
    toDollars(recurringMax, { roundCents: true }),
  );
};

const exhaustiveStatusCheck = (status: never) => {
  console.warn(`Missing order status label for ${status}`);
  return "Unknown";
};

export const getLabelForSubscriptionStatus = (status: UISubscriptionStatus) => {
  switch (status) {
    case UISubscriptionStatus.Active:
      return "Active";
    case UISubscriptionStatus.Canceled:
      return "Canceled";
    case UISubscriptionStatus.Paused:
      return "Paused";
    case UISubscriptionStatus.FailedPayment:
      return "Failed Payment";
    case UISubscriptionStatus.Imported:
      return "Imported";
    default:
      return exhaustiveStatusCheck(status);
  }
};

const exhaustiveColorCheck = (status: never): StatusColor => {
  console.warn(`Missing order status color for ${status}`);
  return "greyscale";
};

export const getColorForSubscriptionStatus = (status: UISubscriptionStatus): StatusColor => {
  switch (status) {
    case UISubscriptionStatus.Active:
      return "success";
    case UISubscriptionStatus.Paused:
      return "warning";
    case UISubscriptionStatus.FailedPayment:
      return "danger";
    case UISubscriptionStatus.Canceled:
    case UISubscriptionStatus.Imported:
      return "greyscale";
    default:
      return exhaustiveColorCheck(status);
  }
};

const isPrimary = (action: UserSubscriptionAction) => action === UserSubscriptionAction.OrderNow;

type SortableSubscriptionAction =
  | UserSubscriptionAction.Skip
  | UserSubscriptionAction.ChangeNextScheduleDate
  | UserSubscriptionAction.Resume
  | UserSubscriptionAction.Cancel;

// Increasing severity: skip (one-time) → change next schedule date (ongoing, reversible) →
// cancel (permanent).
const ACTION_DISPLAY_ORDER: TupleOfEntireUnion<SortableSubscriptionAction> = [
  UserSubscriptionAction.Skip,
  UserSubscriptionAction.ChangeNextScheduleDate,
  UserSubscriptionAction.Resume,
  UserSubscriptionAction.Cancel,
];

const actionIndex = (action: UserSubscriptionAction) => {
  const idx = ACTION_DISPLAY_ORDER.findIndex((a) => a === action);
  return idx === -1 ? ACTION_DISPLAY_ORDER.length : idx;
};

type DynamicUserSubscriptionAction = {
  action: UserSubscriptionAction;
  label: string;
  dest: Destination;
  isPrimary: boolean;
};

// The schedule-date change and skip actions are worded for the clinic's autoship model: v2
// (EDB) clinics schedule deliveries, v1 clinics schedule orders.
// TODO: once AUTOSHIP_EDB is fully rolled out, move the delivery wording into the backend —
// `VISIBLE_ACTIONS_MAP` and the `UserSubscriptionAction` values it maps to
// (app/subscriptions/constants.py) — and delete these overrides.
const CHANGE_NEXT_ORDER_DATE_LABEL = "Change Next Order Date";
const CHANGE_DELIVERY_DATE_LABEL = "Change Delivery Date";
const SKIP_NEXT_DELIVERY_LABEL = "Skip Next Delivery";

type SubscriptionUserActionsOptions = {
  isEdbEnabled: boolean;
};

const getActionLabel = ({
  action,
  backendLabel,
  isEdbEnabled,
}: {
  action: UserSubscriptionAction;
  backendLabel: string;
  isEdbEnabled: boolean;
}): string => {
  if (action === UserSubscriptionAction.ChangeNextScheduleDate) {
    return isEdbEnabled ? CHANGE_DELIVERY_DATE_LABEL : CHANGE_NEXT_ORDER_DATE_LABEL;
  }
  if (action === UserSubscriptionAction.Skip && isEdbEnabled) {
    return SKIP_NEXT_DELIVERY_LABEL;
  }
  return backendLabel;
};

export const getSubscriptionUserActions = (
  subscription: DetailSubscription | ListSubscription,
  { isEdbEnabled }: SubscriptionUserActionsOptions,
): DynamicUserSubscriptionAction[] => {
  const subscriptionActionToLink: Record<UserSubscriptionAction, Destination | undefined> = {
    [UserSubscriptionAction.Resume]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.Resume,
        visible: ModalStatus.Open,
      },
    }),
    [UserSubscriptionAction.Cancel]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.Cancel,
        visible: ModalStatus.Open,
      },
    }),
    [UserSubscriptionAction.Skip]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.SkipNextOrder,
        visible: ModalStatus.Open,
      },
    }),
    // Backed by a MODIFY of the next scheduled order / delivery date via the picker.
    [UserSubscriptionAction.ChangeNextScheduleDate]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.UpdateNextOrder,
        visible: ModalStatus.Open,
      },
    }),
    // Use add to cart button instead
    [UserSubscriptionAction.OrderNow]: undefined,
    // Not suported directly on the client side
    [UserSubscriptionAction.Modify]: undefined,
  };

  return (
    subscription.potential_user_actions
      // Set up the needed values
      .map(([action, label]) => ({
        action,
        label: getActionLabel({ action, backendLabel: label, isEdbEnabled }),
        dest: subscriptionActionToLink[action],
        isPrimary: isPrimary(action),
      }))
      // Remove any actions without a handler
      .flatMap((action) => (action.dest ? [action as typeof action & { dest: Destination }] : []))
      // Sort by display order; primary actions (OrderNow) sort last
      .sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) {
          return a.isPrimary ? 1 : -1;
        }
        return actionIndex(a.action) - actionIndex(b.action);
      })
  );
};

const reasonToCancelLabel: Partial<Record<SubscriptionCancelReason, string>> = {
  [SubscriptionCancelReason.ChangeInTreatment]: "My pet has switched to a different product",
  [SubscriptionCancelReason.Expensive]: "I found a lower price elsewhere",
  [SubscriptionCancelReason.ExcessProduct]: "I have enough supply on hand",
  [SubscriptionCancelReason.SubscriptionNotWanted]: "I prefer to buy this item as needed",
  [SubscriptionCancelReason.AdverseReaction]: "My pet had an adverse reaction to this item",
  [SubscriptionCancelReason.NoLongerClientOfClinic]: "I am no longer a client of this clinic",
  [SubscriptionCancelReason.PetPassedAway]: "My pet has passed away",
  [SubscriptionCancelReason.Other]: "Other",
};

export const getSubscriptionCancelReasonOptions = (): SubscriptionCancelReasonOption[] =>
  Object.entries(reasonToCancelLabel).map(([value, label]) => ({
    value: value as SubscriptionCancelReason,
    label: label as string,
  }));

/** Whether the client gave a cancel reason a retention offer can answer. */
export const isRetentionOfferCancelReason = (
  reason: SubscriptionCancelReason | "",
): reason is RetentionOfferCancelReason =>
  reason === SubscriptionCancelReason.Expensive ||
  reason === SubscriptionCancelReason.SubscriptionNotWanted;

/**
 * The offer's discount, eg. "20%" or "$5". The funding rebate sets exactly one of the percent
 * and cents values, enforced by admin validation.
 */
export const getRetentionOfferDiscountDisplay = ({
  discount_percent: discountPercent,
  discount_cents: discountCents,
}: Pick<RetentionOfferPreview, "discount_percent" | "discount_cents">): string =>
  discountPercent
    ? toPercent(discountPercent)
    : toDollars(discountCents, { collapseWholeDollars: true });

/**
 * The last day that still keeps an offer's discount. `invalid_after_date` is the exclusive
 * instant the offer stops applying, so the deadline is the day holding the moment before it,
 * mirroring how the backend resolves a promotion's exclusive end to a display date. The day is
 * resolved in the client's own timezone, which is the calendar they schedule against.
 */
const getRetentionOfferDeadlineDay = (invalidAfterDate: string): Date =>
  atMidnight(new Date(normalizeToDate(invalidAfterDate).getTime() - 1));

/**
 * The condition on the client's own next order that keeps an offer's discount, eg. "your next
 * order is placed on or before Sep 30, 2026".
 */
const getRetentionOfferDeadlineCondition = (
  invalidAfterDate: string,
  { isEdbEnabled }: { isEdbEnabled: boolean },
): string => {
  const deadline = toDate(getRetentionOfferDeadlineDay(invalidAfterDate));
  return isEdbEnabled
    ? `your next delivery is scheduled on or before ${deadline}`
    : `your next order is placed on or before ${deadline}`;
};

/**
 * The offer's fine print, or undefined for an offer that never expires because its funding rebate
 * has no end date.
 */
export const getRetentionOfferDeadlineFinePrint = (
  { invalid_after_date: invalidAfterDate }: Pick<RetentionOfferPreview, "invalid_after_date">,
  { isEdbEnabled }: { isEdbEnabled: boolean },
): Optional<string> => {
  if (!invalidAfterDate) {
    return undefined;
  }
  const deadline = toDate(getRetentionOfferDeadlineDay(invalidAfterDate));
  return isEdbEnabled
    ? `Offer valid for your next Autoship delivery scheduled by ${deadline}.`
    : `Offer valid for your next Autoship order placed by ${deadline}.`;
};

/**
 * Warns that scheduling for a given date loses an accepted offer's discount, or undefined when
 * the date keeps it.
 */
export const getRetentionOfferDeadlineWarning = ({
  offer,
  scheduleDate,
  isEdbEnabled,
}: {
  offer: Optional<AcceptedRetentionOffer>;
  scheduleDate: Optional<Date | string>;
  isEdbEnabled: boolean;
}): Optional<string> => {
  const invalidAfterDate = offer?.invalid_after_date;
  if (!offer || !invalidAfterDate || !scheduleDate) {
    return undefined;
  }
  if (atMidnight(scheduleDate) <= getRetentionOfferDeadlineDay(invalidAfterDate)) {
    return undefined;
  }
  const condition = getRetentionOfferDeadlineCondition(invalidAfterDate, { isEdbEnabled });
  return (
    `Your ${getRetentionOfferDiscountDisplay(offer)} discount only applies if ${condition}. ` +
    `Scheduling your ${isEdbEnabled ? "delivery" : "order"} for ${toDate(scheduleDate)} means ` +
    "you'll lose this discount."
  );
};

/** How the client will see the offer land, eg. "your next order on Jul 22, 2026" */
export const getRetentionOfferDeliveryDisplay = (
  subscription: DetailSubscription | ListSubscription,
  { isEdbEnabled }: { isEdbEnabled: boolean },
): string => {
  if (isEdbEnabled) {
    return subscription.active_expected_delivery_by
      ? `your next delivery, estimated ${toDate(subscription.active_expected_delivery_by)}`
      : "your next delivery";
  }
  return subscription.next_order_date
    ? `your next order on ${toDate(subscription.next_order_date)}`
    : "your next order";
};

/**
 * Whether an Autoship's payment method would fail at charge time.
 *
 * Matches on the unchargeable statuses rather than `!== Valid` so it fails open: a bundle
 * running against a response without chargeability_status gates nothing, instead of gating
 * every Autoship in the app. The backend failsafe is the source of truth; this is UX.
 */
export const isSubscriptionPaymentUnchargeable = ({
  payment_method,
}: {
  payment_method: Optional<SubscriptionPaymentMethod>;
}) =>
  !payment_method ||
  payment_method.chargeability_status === PaymentMethodChargeabilityStatus.Expired ||
  payment_method.chargeability_status === PaymentMethodChargeabilityStatus.Deleted;

/**
 * Statuses whose next order is close enough for a payment problem to be worth surfacing.
 * A canceled Autoship will never charge, and an imported one has not been set up yet.
 */
const paymentAlertStatuses: UISubscriptionStatus[] = [
  UISubscriptionStatus.Active,
  UISubscriptionStatus.Paused,
];

/** Whether to surface an Autoship's payment problem outside of the forms it blocks. */
export const shouldShowSubscriptionPaymentAlert = (subscription: {
  visible_status: UISubscriptionStatus;
  payment_method: Optional<SubscriptionPaymentMethod>;
}) =>
  paymentAlertStatuses.includes(subscription.visible_status) &&
  isSubscriptionPaymentUnchargeable(subscription);

/**
 * Copy for an Autoship's payment problem. `bannerMessage` warns about it from the Autoship
 * itself, `drawerMessage` explains why a form won't submit, and `actionLabel` names the button
 * that resolves it.
 *
 * A removed card reads differently from a missing one because it stays attached to the
 * Autoship and keeps rendering, so "no payment method" contradicts the card on screen.
 */
export const getSubscriptionPaymentIssueContent = (
  payment_method: Optional<SubscriptionPaymentMethod>,
) => {
  if (!payment_method) {
    return {
      bannerMessage: "This Autoship doesn't have a payment method.",
      drawerMessage:
        "This Autoship doesn't have a payment method. Please add one before making changes.",
      actionLabel: "Add payment method",
    };
  }
  if (payment_method.chargeability_status === PaymentMethodChargeabilityStatus.Deleted) {
    return {
      bannerMessage: "The payment method for this Autoship was removed.",
      drawerMessage:
        "The payment method for this Autoship was removed. Please add a new one before making changes.",
      actionLabel: "Add payment method",
    };
  }
  return {
    bannerMessage: "The payment method for this Autoship has expired.",
    drawerMessage:
      "The payment method for this Autoship has expired. Please update it before making changes.",
    actionLabel: "Update payment method",
  };
};
